import { useState, useEffect } from "react";
import { Package, Loader2, AlertTriangle, CheckCircle2, FileText, Download } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../contexts/AuthContext";
import { Modal } from "./SharedComponents";
import { generateRetiroStockPDF, agruparPorMaterial } from "./helpers";
import { CeldaAsignacionStock, asignacionValida, asignadoTotal } from "./AsignacionStock";   // Fix EV

/**
 * PROMPT BLOQUE 4 (Feb 2026) — Modal "Generar PP — Imputar origen".
 * Por cada ítem del PM se elige el ORIGEN del material: comprar a proveedor
 * (default) o cubrir con stock interno (Materiales/Herramientas). Los ítems a
 * stock NO entran al PP (no se cotizan). NO descuenta stock — solo registra
 * la imputación para trazabilidad (el descuento pertenece al flujo futuro de
 * retiro/entrega de Inventario).
 */
const r4 = (n) => Math.round((Number(n) || 0) * 10000) / 10000;

export function GenerarPPModal({ pm, open, onClose, onGenerated }) {
  // Fix EV (2026-10-02): por material agrupado, el origen y la lista de materiales del stock
  // con su cantidad (uno o varios); lo que no sale del stock sigue a proveedor.
  const [estado, setEstado] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [tools, setTools] = useState([]);
  const [loadingInv, setLoadingInv] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // BLOQUE 24 — tras generar el PP, si hubo ítems a stock interno, se muestra un
  // paso final con el botón para emitir el documento de retiro (aval imprimible).
  const [step, setStep] = useState("form"); // "form" | "done"
  const [submitResult, setSubmitResult] = useState(null);
  const [generatingRSI, setGeneratingRSI] = useState(false);
  const [rsiGenerated, setRsiGenerated] = useState(false);

  useEffect(() => {
    if (!open || !pm) return;
    setEstado([]);
    setStep("form");
    setSubmitResult(null);
    setRsiGenerated(false);
    setLoadingInv(true);
    Promise.all([
      axios.get(`${API}/inventory/materials`, { withCredentials: true }),
      axios.get(`${API}/inventory/tools`, { withCredentials: true }),
    ]).then(([mRes, tRes]) => {
      setMaterials(mRes.data || []);
      setTools(tRes.data || []);
    }).catch(() => toast.error("No se pudo cargar el inventario"))
      .finally(() => setLoadingInv(false));
  }, [open, pm]);

  if (!pm) return null;

  const items = pm.items || [];
  // Fix EN (2026-09-29): "La visualización también debe ser resumida. Veo que el pdf generado
  // para el proveedor sale resumido. Así también debería verse. Esta distribución es netamente
  // para la imputación de los pedidos" (CEN). Una fila por material, como el PDF del PP; el
  // origen elegido vale para todas las líneas del pedido que junta (`__idx`). El envío sigue
  // siendo por línea.
  const grupos = agruparPorMaterial(items);
  const filaDe = (g) => estado[g] || { origen: "proveedor", asign: [] };
  const allStock = grupos.length > 0 && grupos.every((g, gi) => filaDe(gi).origen !== "proveedor" && asignadoTotal(filaDe(gi).asign) >= r4(g.quantity) - 0.0001);
  const listo = grupos.every((g, gi) => asignacionValida(filaDe(gi), Number(g.quantity) || 0, materials));
  const setFila = (gi, fila) => setEstado(prev => { const n = [...prev]; n[gi] = fila; return n; });

  // Fix EV: lo elegido por material se reparte entre las líneas del pedido que junta esa
  // fila, en orden; cada línea manda una imputación por material con su cantidad y lo que
  // no cubren va a proveedor.
  const construirImputaciones = () => {
    const out = [];
    grupos.forEach((g, gi) => {
      const f = filaDe(gi);
      const cola = f.origen === "proveedor" ? [] : (f.asign || []).map(a => ({ id: a.ref?.id, falta: r4(a.cantidad) }));
      g.__idx.forEach(li => {
        let cap = r4(items[li]?.quantity);
        let alguna = false;
        for (const a of cola) {
          if (cap <= 0.0001) break;
          if (a.falta <= 0.0001) continue;
          const q = r4(Math.min(cap, a.falta));
          out.push({ item_idx: li, origen: f.origen, origen_material_id: a.id || null, quantity: q });
          a.falta = r4(a.falta - q);
          cap = r4(cap - q);
          alguna = true;
        }
        if (!alguna) out.push({ item_idx: li, origen: "proveedor", origen_material_id: null });
      });
    });
    return out;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        pm_id: pm._id,
        imputaciones: construirImputaciones(),
      };
      const res = await axios.post(`${API}/purchases/supplier-orders`, payload, { withCredentials: true });
      if (res.data?.pp_created === false) {
        toast.success(res.data.message || "Pedido cubierto por stock interno — no requiere PP");
      } else {
        toast.success(`Pedido de Presupuesto generado: ${res.data.number}`);
      }
      // El backend usa una key distinta según la rama: 'stock_summary' (caso todo-stock,
      // sin PP) vs 'stock_coverage_summary' (caso mixto, PP persistido) — se normaliza acá.
      const stockSummary = res.data?.stock_summary || res.data?.stock_coverage_summary || null;
      if (stockSummary?.count > 0) {
        setSubmitResult({ ...res.data, stock_summary: stockSummary });
        setStep("done");
      } else {
        onGenerated();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar el PP");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateRSI = async () => {
    setGeneratingRSI(true);
    try {
      const res = await axios.post(`${API}/purchases/stock-withdrawals`, { pm_id: pm._id }, { withCredentials: true });
      generateRetiroStockPDF(res.data);
      toast.success(`Solicitud de retiro de stock interno generada: ${res.data.number}`);
      setRsiGenerated(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar la solicitud de retiro");
    } finally {
      setGeneratingRSI(false);
    }
  };

  // Fix EN (2026-09-29): lo imputado a stock también se muestra por material.
  const stockResumen = Object.values((submitResult?.stock_summary?.items || []).reduce((acc, it) => {
    const k = [it.material_name, it.unit, it.origen, it.origen_material_code].join("|");
    acc[k] = acc[k]
      ? { ...acc[k], quantity: Math.round((acc[k].quantity + (Number(it.quantity) || 0)) * 10000) / 10000 }
      : { ...it, quantity: Number(it.quantity) || 0 };
    return acc;
  }, {}));

  return (
    <Modal open={open} onClose={onClose} title={step === "done" ? `PP Generado · ${pm.number}` : `Generar PP — Imputar Origen · ${pm.number}`} extraWide>
      {step === "done" ? (
        <div className="space-y-4" data-testid="genpp-done-step">
          <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">
                {submitResult?.pp_created === false ? "Pedido cubierto por stock interno" : `PP generado: ${submitResult?.number}`}
              </p>
              <p className="text-xs text-emerald-600 mt-0.5">
                {stockResumen.length} ítem{stockResumen.length !== 1 ? "s" : ""} fueron imputados a stock interno (Materiales/Herramientas).
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="genpp-rsi-panel">
            <p className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-2">Aval documentado — Retiro de Stock Interno</p>
            {/* Fix FB (2026-10-06): la solicitud de retiro ya se generó junto con el PP. */}
            <p className="text-xs text-amber-700 mb-3" data-testid="genpp-rsi-texto">
              {submitResult?.rsi_number
                ? <>Solicitud de retiro <strong>{submitResult.rsi_number}</strong> generada: ya está en Inventario → Pedido de Movimiento para aprobar. Podés descargar el aval imprimible.</>
                : "Generá el documento imprimible de solicitud de retiro para los ítems cubiertos por stock interno (material/herramienta), a modo de aval firmable. Este documento NO descuenta el inventario."}
            </p>
            <div className="space-y-1 mb-3">
              {stockResumen.map((it, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-1.5 border border-amber-100">
                  <span className="font-medium text-gray-700">{it.material_name} <span className="text-gray-400">({it.quantity} {it.unit})</span></span>
                  <span className="text-amber-700 font-semibold">{it.origen === "stock_herramientas" ? "Herramienta" : "Material"}: {it.origen_material_code} · {it.origen_material_name}</span>
                </div>
              ))}
            </div>
            <button type="button" onClick={handleGenerateRSI} disabled={generatingRSI}
              className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60 transition-all"
              data-testid="generate-rsi-btn">
              {generatingRSI ? <Loader2 size={15} className="animate-spin" /> : rsiGenerated ? <Download size={15} /> : <FileText size={15} />}
              {generatingRSI ? "Generando..." : rsiGenerated ? "Descargar nuevamente (PDF)" : submitResult?.rsi_number ? "Descargar solicitud de retiro (PDF)" : "Generar solicitud de retiro de stock interno"}
            </button>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onGenerated}
              className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold transition-all"
              data-testid="genpp-done-close-btn">
              Cerrar
            </button>
          </div>
        </div>
      ) : (
      <div className="space-y-4">
        <p className="text-xs text-gray-500">
          Indicá para cada material si se compra a proveedor o se cubre con stock interno, de qué material y cuánto (puede
          salir de varios, por ejemplo un rollo de cada marca). Lo imputado a stock <strong>no se incluye en el PP</strong>; lo que no alcanza sigue a proveedor.
        </p>

        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="text-gray-500 uppercase text-[10px]">
                <th className="py-2 px-2 text-left w-8">#</th>
                <th className="py-2 px-2 text-left">Descripción</th>
                <th className="py-2 px-2 text-left w-14">UM</th>
                <th className="py-2 px-2 text-left w-16">Cant.</th>
                <th className="py-2 px-2 text-left w-28">Marca Rec.</th>
                <th className="py-2 px-2 text-left w-96">Origen · material y cantidad a stock</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {grupos.map((item, idx) => {   // Fix EN: `item` es el material agrupado
                const f = filaDe(idx);
                return (
                  <tr key={idx} className={f.origen !== "proveedor" ? "bg-amber-50/40" : ""} data-testid={`genpp-item-row-${idx}`}>
                    <td className="py-2 px-2 font-bold text-[#2E6DA4] align-top">{idx + 1}</td>
                    <td className="py-2 px-2 font-medium text-gray-800 align-top">
                      {item.material_name}
                      {item.__lineas > 1 && <span className="block text-[10px] font-normal text-gray-400" data-testid={`genpp-item-lineas-${idx}`}>Total de {item.__lineas} ítems del presupuesto</span>}
                    </td>
                    <td className="py-2 px-2 text-gray-500 align-top">{item.unit}</td>
                    <td className="py-2 px-2 font-semibold text-gray-900 align-top">{item.quantity}</td>
                    <td className="py-2 px-2 text-gray-500 italic align-top">{item.marca_recomendada || "—"}</td>
                    <td className="py-2 px-2 align-top">
                      {loadingInv && f.origen !== "proveedor" ? (
                        <p className="flex items-center gap-2 text-xs text-gray-400"><Loader2 size={14} className="animate-spin" /> Cargando inventario...</p>
                      ) : (
                        <CeldaAsignacionStock fila={f} total={Number(item.quantity) || 0} unidad={item.unit} onChange={fila => setFila(idx, fila)}
                          materials={materials} tools={tools} testId={`genpp-${idx}`} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {allStock && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">Todos los ítems fueron imputados a stock interno — no se generará un PP. El pedido quedará marcado como "Cubierto por stock interno".</p>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} disabled={submitting}
            className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting || !listo}
            className={`flex-1 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all text-white ${allStock ? "bg-amber-600 hover:bg-amber-700" : "bg-[#2E6DA4] hover:bg-[#245A8A]"}`}
            data-testid="genpp-confirm-btn">
            {submitting ? <Loader2 size={15} className="animate-spin" /> : allStock ? <CheckCircle2 size={15} /> : <Package size={15} />}
            {submitting ? "Procesando..." : allStock ? "Confirmar imputación (sin PP)" : "Generar PP"}
          </button>
        </div>
      </div>
      )}
    </Modal>
  );
}
